import { Typography } from '../../../components/typography/typography';
import Icon from '../../../components/icons/icon';
import { Divider } from '../../../components/divider/divider';
import { Currency, Dinero } from 'dinero.js';
import React, { ReactNode, useState } from 'react';
import { Collapsible } from '../../../components/collapsible/collapsible';
import { dineroSum } from '../../../util/util';
import { EditableField } from '../../../components/field/field';
import { ConfirmModal } from '../../../components/modal/confirm-modal';

type OrderSummaryLineItemProps = {
  title: string;
  isAmountEditable?: boolean;
  isNameEditable?: boolean;
  isDeletable?: boolean;
  value: Dinero;
  bold?: boolean;
  onDelete?: () => void;
  onChange?: (title: string, value: number) => void;
};
export const OrderSummary = ({
  title = 'Order Summary',
  lineItems,
  defaultCurrency,
}: {
  title?: string;
  lineItems: OrderSummaryLineItemProps[];
  defaultCurrency: Currency;
}) => {
  return (
    <div className="flex justify-end">
      <div className="flex-[35%] grow-0">
        <Collapsible
          openAtStart
          trigger={(isOpen) => (
            <div className="bg-grey-800 rounded-lg p-2 h-9 flex justify-between items-center cursor-pointer">
              <Typography type="text-sm" weight="medium" className="text-quipli-white">
                {title}
              </Typography>

              <Icon icon={isOpen ? 'ChevronUp' : 'ChevronDown'} stroke="var(--quipli-white)" />
            </div>
          )}
        >
          <div className="flex flex-col pt-3 gap-3">
            <Divider />

            {lineItems.map((lineItem) => (
              <OrderSummaryLineItem {...lineItem} />
            ))}

            <OrderSummaryLineItem
              bold
              title="Total"
              value={dineroSum(
                lineItems.map((l) => l.value),
                defaultCurrency,
              )}
            />
          </div>
        </Collapsible>
      </div>
    </div>
  );
};

const OrderSummaryLineItem = ({
  title,
  value,
  bold = false,
  isNameEditable = false,
  isAmountEditable = false,
  isDeletable = false,
  onDelete,
  onChange,
}: OrderSummaryLineItemProps) => {
  const [modal, setModal] = useState<ReactNode>();

  return (
    <>
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-2">
          {isNameEditable ? (
            <EditableField value={title} onChange={(v) => onChange?.(v, value.toUnit())} type="text" />
          ) : (
            <Typography type="text-sm" weight={bold ? 'semi-bold' : 'regular'} className="text-grey-800">
              {title}
            </Typography>
          )}
          {isDeletable && (
            <div
              className="cursor-pointer"
              onClick={() =>
                setModal(
                  <ConfirmModal
                    prompt={`Remove ${title}?`}
                    additionalText="This cannot be undone"
                    noLabel="Cancel"
                    yesLabel="Remove"
                    yesDataCy="delete-cell-confirm"
                    yesButtonYellow
                    isOpen
                    headerIcon={
                      <div className="flex justify-center">
                        <Icon icon="InfoCircle" size="xlarge" stroke="var(--error-500)" />
                      </div>
                    }
                    onConfirm={() => {
                      onDelete?.();
                      setModal(undefined);
                    }}
                    close={() => setModal(undefined)}
                  />,
                )
              }
            >
              <Icon icon="Trash" />
            </div>
          )}
        </div>

        {isAmountEditable ? (
          <EditableField
            value={value.toUnit()}
            formattedValue={value.toFormat()}
            onChange={(v) => onChange?.(title, v)}
            type="number"
          />
        ) : (
          <Typography type="text-sm" weight={bold ? 'semi-bold' : 'regular'} className="text-grey-800">
            {value.toFormat()}
          </Typography>
        )}
      </div>

      {modal}
    </>
  );
};
